/**
 * ClassSwitcherTopBar — OPT-IN top-of-screen quick class switcher.
 *
 * QA/dev convenience: click any class button to instantly respawn as that
 * class without going back through the pre-game funnel. It inherited its
 * visual language from the retired QuickClassPickerScreen (deleted
 * 2026-08-06) and is now the only class-grid surface left in the app.
 *
 * Visibility gate (DJ 2026-07-10 — it was always-on and leaked onto every
 * mode incl. /adventure and /gmrace): renders ONLY with `?classSwitcher=1`.
 * Mode configs opt in via `ModeUrlConfig.classSwitcher` (currently the dev
 * surfaces /debug + /abilitygrounds); QA harnesses can force it per-URL on
 * any mode. The param survives the switcher's own reload navigation since
 * navigateWithClass preserves the query string.
 *
 * Mechanism: navigates the page to the current URL with `?class=X` and
 * `?pickCharacter=1` injected. PreGameFlow's funnel then resolves a REAL
 * server-persisted character through `resolveUrlClassCharacter`, reusing an
 * existing hero of that class before minting one. This works identically on
 * localhost and on Mongo-backed surfaces (PTR / QA tunnel).
 *
 * Until 2026-08-07 it sent `pickCharacter=0` and went through useAuth's
 * synthetic dev spawn instead, which stood the switch down on both tiers —
 * see buildClassSwitchUrl for the full two-reason history.
 *
 * A reload, not an in-place swap: that avoids the risk surface of mid-room
 * class swap (ability registry refresh, sprite re-texture, schema mutation).
 *
 * Visibility: only renders when `currentClass` is provided (i.e. player is
 * spawned + in a room). Hidden on PreGame screens by virtue of being
 * mounted inside the `currentView === 'game'` branch in main.tsx.
 *
 * Position: fixed top-center, low z-index relative to modal overlays
 * (DeathOverlay, DrainOverlay) so respawn / drain UI takes precedence.
 */

import React from 'react';

import { CharacterClass } from '../../../shared/CharacterTypes';
import { gameLogger } from '../../../utils/GameLogger';
import { COLORS, classColor, classIcon } from '../theme';
import { getAllProductCallings } from '../../../shared/classes/CallingDisplayRegistry';
import { bootHref, bootSearchParams } from '../../../config/boot/bootUrl';

interface ClassButton {
  key: CharacterClass;
  label: string;
  emoji: string;
  color: string;
}

const CLASS_BUTTONS: ClassButton[] = [
  ...getAllProductCallings().map((c) => ({
    key: c.id,
    label: c.name,
    emoji: c.icon,
    color: c.color,
  })),
  {
    key: CharacterClass.DEV,
    label: 'Dev',
    emoji: classIcon(CharacterClass.DEV),
    color: classColor(CharacterClass.DEV),
  },
];

function getCurrentClass(): CharacterClass | null {
  try {
    const w = (globalThis as any).window;
    const room = w?.nm?.getRoom?.();
    const me = room?.state?.players?.get?.(room?.sessionId);
    const cls = me?.heroClass;
    if (typeof cls !== 'string') return null;
    const match = (Object.values(CharacterClass) as string[]).find((v) => v === cls);
    return (match as CharacterClass) ?? null;
  } catch {
    return null;
  }
}

/**
 * The URL contract of a class switch, as a pure function of the current href.
 *
 * Exported and separated from the navigation so it can be asserted directly:
 * the property that matters is that the RESULT is a URL the auto-pick acts on,
 * and that is checkable without driving a jsdom navigation (`window.location`
 * is non-configurable, so the navigation itself cannot be intercepted).
 */
export function buildClassSwitchUrl(currentHref: string, klass: CharacterClass): string {
  const url = new URL(currentHref);
  url.searchParams.set('class', klass);
  // `pickCharacter=1` routes the switch through PreGameFlow's funnel, which
  // resolves a REAL server-persisted character via `resolveUrlClassCharacter`
  // (reuse-before-create: an existing hero of that class wins, otherwise one
  // is minted). It is deliberately NOT `0`.
  //
  // `pickCharacter=0` was the old value and it stood the switch down on BOTH
  // tiers, for two different reasons that happened to coincide (fixed
  // 2026-08-07, DJ-approved; the gap pre-dated the 2026-08-06 picker retirement
  // and was recorded rather than fixed there because it changes localhost
  // identity semantics):
  //   - On a Mongo-backed surface useAuth's synthetic dev spawn returns null,
  //     because it is gated on `isStrictLocalhost || !multiplayer`.
  //   - AND `pick === '0'` is a hard stand-down for the URL auto-pick — the
  //     2026-07-09 quest-loss fix made that param mean "another flow owns the
  //     join". So both paths declined and the switch landed on the roster.
  // On localhost the synthetic path did work, but it minted a `devplayer_*`
  // id, which is exactly what a Mongo-backed room kicks as `NO_CHARACTER`.
  //
  // Semantics this changes on localhost: a switch now RESUMES your existing
  // hero of that class (level, gear, quests) instead of handing you a fresh
  // synthetic one. That is the point — it is the same character the room and
  // the roster already agree on.
  url.searchParams.set('pickCharacter', '1');
  // A class switch is an explicit NEW-character intent — it must beat the
  // keepCharacter bridge (teleport / editor-Test flows leave keepCharacter=1
  // sticky in the URL; PreGameFlow's bridge then re-selects the LAST character
  // and silently ignores ?class — the "switcher does nothing" bug, 2026-06-11).
  url.searchParams.delete('keepCharacter');
  // `/intro` keeps createCharacter=1 in the address bar after creation. That
  // intent has higher precedence than `?class=` in PreGameFlow, so preserving
  // it would reopen the creator instead of switching. A class-switch click is
  // the new entry intent and must clear both older bridges.
  url.searchParams.delete('createCharacter');
  return url.toString();
}

function navigateWithClass(klass: CharacterClass): void {
  try {
    const w = (globalThis as any).window;
    if (!w?.location) return;
    const target = buildClassSwitchUrl(bootHref(), klass);
    gameLogger.info('character', `🔁 Class switch → ${klass}; reloading: ${target}`);
    w.location.href = target;
  } catch (e) {
    gameLogger.error('character', 'Class switch navigation failed', { error: String(e) });
  }
}

/** The opt-in gate — default OFF everywhere; `?classSwitcher=1` enables
 *  (mode configs set it via coerceModeUrl, harnesses per-URL). Read once:
 *  the param can't change without a navigation. */
function isSwitcherEnabled(): boolean {
  try {
    return bootSearchParams().get('classSwitcher') === '1';
  } catch {
    return false;
  }
}

export const ClassSwitcherTopBar: React.FC = () => {
  const [enabled] = React.useState(isSwitcherEnabled);
  // Poll every 500ms — same cadence as DrainOverlay's poll-bind-room.
  // Cheaper than wiring an EventBus subscription for a small dev surface.
  const [currentClass, setCurrentClass] = React.useState<CharacterClass | null>(null);
  React.useEffect(() => {
    if (!enabled) return;
    const tick = () => setCurrentClass(getCurrentClass());
    tick();
    const id = setInterval(tick, 500);
    return () => clearInterval(id);
  }, [enabled]);

  if (!enabled) return null;

  // Hide until the player is bound to the room. Avoids flash on splash /
  // PreGame, and keeps the bar unambiguous (always shows your current class
  // highlighted).
  if (!currentClass) return null;

  return (
    <div
      data-testid="class-switcher-top-bar"
      data-current-class={currentClass}
      style={{
        position: 'fixed',
        top: 8,
        left: '50%',
        transform: 'translateX(-50%)',
        display: 'flex',
        // gap+padding bumped 2026-05-01 for breathing room — was 6/6+8.
        // Visual style stays aligned with LobbyInfoPanel directly below
        // (same bg / border / blur / radius) so the two bars read as
        // one family stack.
        gap: 8,
        padding: '6px 10px',
        background: 'rgba(10, 10, 14, 0.78)',
        border: '1px solid rgba(255, 255, 255, 0.12)',
        borderRadius: 8,
        zIndex: 50,
        backdropFilter: 'blur(4px)',
      }}
    >
      {CLASS_BUTTONS.map((b) => {
        const isCurrent = b.key === currentClass;
        return (
          <button
            key={b.key}
            type="button"
            onClick={() => { if (!isCurrent) navigateWithClass(b.key); }}
            disabled={isCurrent}
            title={isCurrent ? `Current: ${b.label}` : `Switch to ${b.label}`}
            data-class-key={b.key}
            data-current={isCurrent ? 'true' : 'false'}
            style={{
              width: 38,
              height: 38,
              borderRadius: 6,
              border: isCurrent ? `2px solid ${b.color}` : '1px solid rgba(255, 255, 255, 0.15)',
              background: isCurrent ? b.color : 'rgba(20, 20, 26, 0.85)',
              color: COLORS.textPrimary,
              fontSize: 20,
              cursor: isCurrent ? 'default' : 'pointer',
              opacity: isCurrent ? 1 : 0.85,
              transition: 'opacity 0.12s, background 0.12s',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: 0,
            }}
            onMouseEnter={(e) => { if (!isCurrent) e.currentTarget.style.opacity = '1'; }}
            onMouseLeave={(e) => { if (!isCurrent) e.currentTarget.style.opacity = '0.85'; }}
          >
            {b.emoji}
          </button>
        );
      })}
    </div>
  );
};

export default ClassSwitcherTopBar;
